import { useState, useRef, useEffect } from 'react'
import { useMutation } from '@tanstack/react-query'
import {
  ChatBubbleLeftRightIcon,
  SparklesIcon,
  ArrowPathIcon,
  PaperAirplaneIcon,
  ChartBarIcon,
  CubeIcon,
  UsersIcon,
  BanknotesIcon,
  LightBulbIcon,
  ExclamationCircleIcon,
  CreditCardIcon,
  ArrowTrendingUpIcon,
  CalculatorIcon,
  TruckIcon,
  TagIcon,
} from '@heroicons/react/24/outline'
import { aiService } from '../../services/aiService'

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  type?: 'text' | 'chart' | 'table'
  data?: any
}

const SUGGESTIONS = [
  { icon: ChartBarIcon, text: 'What were my top selling products this month?' },
  { icon: ArrowTrendingUpIcon, text: 'Show me sales revenue trend for the last 30 days' },
  { icon: CubeIcon, text: 'Which products are low in stock?' },
  { icon: UsersIcon, text: 'Who are my top customers?' },
  { icon: LightBulbIcon, text: 'Analyze my inventory turnover' },
  { icon: BanknotesIcon, text: 'What is my profit margin by category?' },
  { icon: ExclamationCircleIcon, text: 'Show me outstanding payments from customers' },
  { icon: CreditCardIcon, text: 'What is my payment method breakdown?' },
  { icon: CubeIcon, text: 'Show me active consignments' },
  { icon: CalculatorIcon, text: 'What is my average order value?' },
  { icon: TagIcon, text: 'Show me sales by brand' },
  { icon: CubeIcon, text: 'Which products are slow moving?' },
  { icon: ArrowTrendingUpIcon, text: 'Compare today vs yesterday sales' },
  { icon: CalculatorIcon, text: 'What is my current stock valuation?' },
  { icon: UsersIcon, text: 'Show me new vs returning customers' },
  { icon: TruckIcon, text: 'Show me pending purchase orders' },
]

export const AIAssistantPage = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      role: 'assistant',
      content: 'Hello! I\'m your AI assistant. Ask me anything about your business data - sales, inventory, customers, or financial reports. I can analyze trends, generate reports, and provide insights.',
    },
  ])
  const [input, setInput] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const queryMutation = useMutation({
    mutationFn: (query: string) => aiService.query(query),
    onSuccess: (response) => {
      const result = response.data?.data?.result
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        role: 'assistant',
        content: result?.summary || 'Here are your results:',
        type: result?.type || 'text',
        data: result?.data,
      }])
    },
    onError: () => {
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        role: 'assistant',
        content: 'I apologize, but I encountered an error processing your request. Please try again or rephrase your question.',
        type: 'text',
      }])
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!input.trim()) return

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
    }

    setMessages(prev => [...prev, userMessage])
    queryMutation.mutate(input)
    setInput('')
  }

  const handleSuggestion = (text: string) => {
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: text,
    }

    setMessages(prev => [...prev, userMessage])
    queryMutation.mutate(text)
  }

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  return (
    <div className="h-[calc(100vh-6rem)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <SparklesIcon className="w-7 h-7 text-primary-600" />
            AI Assistant
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Ask questions about your business data and get instant insights
          </p>
        </div>
        <button
          onClick={() => {
            setMessages([{
              id: 'welcome',
              role: 'assistant',
              content: 'Hello! I\'m your AI assistant. Ask me anything about your business data - sales, inventory, customers, or financial reports. I can analyze trends, generate reports, and provide insights.',
            }])
          }}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <ArrowPathIcon className="w-4 h-4" />
          New Chat
        </button>
      </div>

      {/* Chat Container */}
      <div className="flex-1 bg-white rounded-xl shadow border border-gray-100 flex flex-col overflow-hidden">
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex gap-4 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                message.role === 'user' 
                  ? 'bg-primary-600 text-white' 
                  : 'bg-gradient-to-br from-purple-500 to-pink-500 text-white'
              }`}>
                {message.role === 'user' ? (
                  <ChatBubbleLeftRightIcon className="w-4 h-4" />
                ) : (
                  <SparklesIcon className="w-4 h-4" />
                )}
              </div>
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                message.role === 'user'
                  ? 'bg-primary-600 text-white rounded-tr-sm'
                  : 'bg-gray-100 text-gray-900 rounded-tl-sm'
              }`}>
                <p className="whitespace-pre-wrap">{message.content}</p>
                {message.data && message.type === 'table' && (
                  <div className="mt-3 overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          {Object.keys(message.data[0] || {}).map(key => (
                            <th key={key} className="px-3 py-2 text-left font-medium">
                              {key.replace(/_/g, ' ')}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {message.data.map((row: any, i: number) => (
                          <tr key={i} className="border-b border-gray-100">
                            {Object.values(row).map((val: any, j: number) => (
                              <td key={j} className="px-3 py-2">
                                {typeof val === 'number' 
                                  ? val.toLocaleString() 
                                  : String(val)
                                }
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {message.data && message.type === 'chart' && (
                  <div className="mt-3 h-40 flex items-end gap-2">
                    {message.data.map((point: any, i: number) => (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1">
                        <div 
                          className="w-full bg-primary-400 rounded-t"
                          style={{ 
                            height: `${(point.value / Math.max(...message.data.map((d: any) => d.value))) * 100}px` 
                          }}
                        />
                        <span className="text-xs text-gray-500">{point.label}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          {queryMutation.isPending && (
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <SparklesIcon className="w-4 h-4 text-white" />
              </div>
              <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-4 py-3">
                <div className="flex items-center gap-2 text-gray-500">
                  <div className="animate-pulse flex gap-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full" />
                    <div className="w-2 h-2 bg-gray-400 rounded-full" />
                    <div className="w-2 h-2 bg-gray-400 rounded-full" />
                  </div>
                  <span className="text-sm">Analyzing your data...</span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Suggestions */}
        {messages.length <= 2 && (
          <div className="px-4 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-500 mb-2">Try asking:</p>
            <div className="flex flex-wrap gap-2">
              {SUGGESTIONS.map((suggestion, i) => (
                <button
                  key={i}
                  onClick={() => handleSuggestion(suggestion.text)}
                  className="flex items-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm text-gray-700 transition-colors"
                >
                  <suggestion.icon className="w-4 h-4 text-primary-600" />
                  {suggestion.text}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <div className="p-4 border-t border-gray-100">
          <form onSubmit={handleSubmit} className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask me anything about your business..."
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
            <button
              type="submit"
              disabled={!input.trim() || queryMutation.isPending}
              className="px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              <PaperAirplaneIcon className="w-5 h-5" />
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
